import { api } from './client';
import { compressImage } from '@/lib/compressImage';

export interface PickListItem {
  id: number;
  doItemId: number | null;
  itemId: number | null;
  itemCode: string | null;
  itemName: string;
  unit: string;
  doQty: string;
  pickedQty: string;
  isChecked: boolean;
  discrepancy: string;
  discrepancyReason: string | null;
  currentStock: number;
  availableStock?: number;
  imageUrl?: string | null;
  barcode?: string | null;
  locations: { code: string; qty: number }[];
}

export interface PickList {
  id: number;
  pickNo: string;
  doId: number;
  pickDate: string;
  assignedTo: string;
  supervisor: string | null;
  status: 'In Progress' | 'Completed' | 'Cancelled';
  supervisorConfirmed: boolean;
  confirmedAt: string | null;
  remarks: string | null;
  /** Photos of the segregated items — JSON array of upload URLs (see pickListPhotos). */
  photoUrls?: string | null;
  items: PickListItem[];
  dispatchOrder?: {
    id: number;
    doNo: string;
    /** DO raised on a part-approved order. */
    isPartial?: boolean;
    partyId?: number;
    remarks?: string | null;
    party?: { name: string };
    enquiry?: { enqNo: string; source?: string | null; remarks?: string | null; balancePending?: boolean };
  };
  packing?: { id: number; packNo: string; status: string }[];
  onlineCustomer?: string | null;
  /** Shopify order number + buyer, on a single pick list of an online order. */
  onlineOrder?: { orderName: string | null; customerName: string | null } | null;
}

export async function listPickLists(filter: { status?: string; doId?: number; assignedTo?: string; channel?: string; partial?: 'partial' | 'full'; from?: string; to?: string } = {}): Promise<PickList[]> {
  const r = await api.get<PickList[]>('/pick-list', { params: filter });
  return r.data;
}

/** Excel export of every pick list matching the filters + search (no cap). */
export function pickListExportPath(filter: { status?: string; channel?: string; partial?: 'partial' | 'full'; from?: string; to?: string; search?: string }): string {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(filter).filter(([, v]) => v != null && v !== '')) as Record<string, string>,
  ).toString();
  return `/pick-list/export.xlsx${qs ? `?${qs}` : ''}`;
}

export async function getPickList(id: number): Promise<PickList> {
  const r = await api.get<PickList>(`/pick-list/${id}`);
  return r.data;
}

export async function updatePickList(
  id: number,
  payload: {
    assignedTo?: string;
    supervisor?: string;
    remarks?: string;
    items?: { pickListItemId: number; pickedQty: number; isChecked?: boolean; discrepancyReason?: string }[];
  },
): Promise<PickList> {
  const r = await api.patch<PickList>(`/pick-list/${id}`, payload);
  return r.data;
}

export async function confirmPickList(id: number, supervisor: string): Promise<PickList> {
  const r = await api.post<PickList>(`/pick-list/${id}/confirm`, { supervisor });
  return r.data;
}

/** Photos of the segregated items allowed on one Pick List (matches the backend). */
export const MAX_PICK_PHOTOS = 5;
export function pickListPhotos(pl: { photoUrls?: string | null } | null | undefined): string[] {
  if (!pl?.photoUrls) return [];
  try { const a = JSON.parse(pl.photoUrls); return Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string') : []; } catch { return []; }
}
export async function uploadPickListPhotos(id: number, files: (File | Blob)[]): Promise<{ photoUrls: string[] }> {
  // Shrink each photo first so the Packing and Dispatch Note documents stay light.
  const small = await Promise.all(files.map((f) => compressImage(f)));
  const fd = new FormData();
  small.forEach((f, i) => fd.append('image', f, f instanceof File && f.name ? f.name : `photo-${i + 1}.png`));
  const r = await api.post(`/pick-list/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return r.data;
}
export async function removePickListPhoto(id: number, url: string): Promise<{ photoUrls: string[] }> {
  const r = await api.delete(`/pick-list/${id}/photos`, { data: { url } });
  return r.data;
}
